/**
 * Fixed-timestep accumulator. Converts variable frame deltas into a whole number of
 * simulation steps plus an interpolation factor, so game speed is independent of the
 * display refresh rate. Excess backlog beyond `maxStepsPerFrame` is dropped (the game
 * slows down instead of spiralling when the machine can't keep up).
 */
export class FixedStepLoop {
	private accumulator = 0;

	constructor(
		readonly stepMs: number,
		readonly maxStepsPerFrame = 8,
		/** Frame deltas above this (tab switch, breakpoint) are clamped. */
		readonly maxFrameMs = 250,
	) {}

	/** Adds elapsed time and returns how many steps to simulate now. */
	advance(deltaMs: number): number {
		this.accumulator += Math.min(Math.max(deltaMs, 0), this.maxFrameMs);
		let steps = Math.floor(this.accumulator / this.stepMs);
		if (steps > this.maxStepsPerFrame) {
			steps = this.maxStepsPerFrame;
			this.accumulator = this.stepMs * steps; // drop the backlog
		}
		this.accumulator -= steps * this.stepMs;
		return steps;
	}

	/** Progress towards the next step in [0, 1): how far to interpolate between states. */
	get alpha(): number {
		return this.accumulator / this.stepMs;
	}

	reset(): void {
		this.accumulator = 0;
	}
}
